import request from "superagent";
import env from "../constants/env";
import { Auth } from "aws-amplify";

function sessionError(message, code) {
  return Object.assign(new Error(`${message} (${code})`), { code });
}

async function currentIdToken() {
  let session;
  try {
    // Cognito renews expired sessions here; app state can retain the ID token
    // from before an idle tab or the periodic credentials refresh.
    session = await Auth.currentSession();
  } catch (error) {
    const code = error && (error.code || error.name);
    const message = typeof error === "string" ? error : error && error.message;
    const missingSession = [
      "No current user",
      "Cannot retrieve a new session. Please authenticate.",
      "Local storage is missing an ID Token, Please authenticate",
      "Username is null. Cannot retrieve a new session"
    ].includes(message);
    if (code === "NotAuthorizedException" || missingSession) {
      throw sessionError("Sign in again before managing job chats.", "JOB_CHAT_AUTH_REQUIRED");
    }
    throw sessionError("Unable to refresh your sign-in session. Check your connection and try again.", "JOB_CHAT_SESSION_UNAVAILABLE");
  }
  const idToken = session && session.getIdToken && session.getIdToken();
  const jwt = idToken && idToken.getJwtToken && idToken.getJwtToken();
  if (typeof jwt !== "string" || !jwt.trim()) {
    throw sessionError("Sign in again before managing job chats.", "JOB_CHAT_AUTH_REQUIRED");
  }
  return jwt;
}

export async function jobChatRequest(action, body) {
  const jwt = await currentIdToken();
  try {
    const response = await request.post(`${env.apiGateway.baseUrl}/services/streamChat/jobChats/${action}`)
      .timeout({response:25000,deadline:27000})
      .set("Authorization", jwt).set("Accept", "application/json").send(body);
    return response.body;
  } catch (error) {
    const code = error.response && error.response.body && error.response.body.code;
    if (error.status === 401 && ["JOB_CHAT_AUTH_REQUIRED", "JOB_CHAT_AUTH_INVALID", "JOB_CHAT_AUTH_UNTRUSTED"].includes(code)) {
      throw sessionError("Your sign-in session was rejected. Sign in again, then retry.", code);
    }
    // Never replay a submitted archive/restore after an ambiguous response.
    // The existing signed continuation and explicit Resume own retry behavior.
    throw error;
  }
}

// The server checks access under the job's indexing lock without waiting (the
// search indexer shares that lock), so JOB_CHAT_BUSY means "try again shortly",
// not a denial. Opening a job chat sends several guarded Stream requests at once
// (watch, mark read), so this tab checks one job at a time and retries a busy
// answer briefly. Results are never shared or cached: each request is checked.
const ACCESS_BUSY_RETRY_DELAYS_MS = [200, 500, 1000, 2000];
const accessChecksByJob = new Map();

function isJobChatBusy(error) {
  return Boolean(error && error.status === 409 && error.response && error.response.body
    && error.response.body.code === "JOB_CHAT_BUSY");
}

async function checkAccess(body, retryDelaysMs) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await jobChatRequest("access", body);
    } catch (error) {
      if (!isJobChatBusy(error) || attempt >= retryDelaysMs.length) throw error;
      await new Promise(resolve => setTimeout(resolve, retryDelaysMs[attempt]));
    }
  }
}

export function jobChatAccess(orgId, instanceId, channelId = `instance-${instanceId}`, { busyRetryDelaysMs = ACCESS_BUSY_RETRY_DELAYS_MS } = {}) {
  const jobKey = `${orgId}:${instanceId}`;
  const previous = accessChecksByJob.get(jobKey) || Promise.resolve();
  const check = previous.then(() => checkAccess({ orgId, instanceId, channelId }, busyRetryDelaysMs));
  const settled = check.catch(() => null);
  accessChecksByJob.set(jobKey, settled);
  settled.then(() => {
    if (accessChecksByJob.get(jobKey) === settled) accessChecksByJob.delete(jobKey);
  });
  return check;
}
